import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Box, Typography, TextField, MenuItem, Select, Button, IconButton, FormControl, InputLabel, Checkbox, FormControlLabel } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import NewsHeaderEditModal from '../../components/News/NewsHeaderEditModal';
import ConfirmModal from '../../components/common/ConfirmModal';
import { PATHS } from '../../routes/paths';
import {
  getNewsHeadersApi,
  createNewsApi,
  updateNewsApi,
  uploadNewsImageApi,
  uploadNewsVideoApi,
  cleanupNewsUploadsApi,
  getNewsDetailApi,
  getServerBaseUrl,
} from '../../api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Quill from 'quill';
import ImageResize from 'quill-image-resize-module-react';

// quill-image-resize-module-react 내부에서 window.Quill.find(...)를 사용합니다.
// react-quill(v2) + ESModule 환경에서는 window.Quill이 undefined일 수 있어 명시적으로 연결합니다.
if (typeof window !== 'undefined' && !window.Quill) {
  window.Quill = Quill;
}

Quill.register('modules/imageResize', ImageResize);

const MAX_VIDEO_BYTES = 20 * 1024 * 1024; // 20MB

const BlockEmbed = Quill.import('blots/block/embed');
class CustomVideoBlot extends BlockEmbed {
  static create(value) {
    const node = super.create();
    const payload = typeof value === 'string' ? { src: value } : value || {};
    const { src, poster } = payload;

    node.setAttribute('controls', true);
    node.setAttribute('playsinline', true);
    node.setAttribute('preload', 'metadata');
    node.setAttribute('controlsList', 'nodownload');
    node.style.width = '100%';
    node.style.height = 'auto';
    node.style.display = 'block';
    node.style.objectFit = 'contain';

    if (src) node.setAttribute('src', src);
    if (poster) node.setAttribute('poster', poster);

    return node;
  }

  static value(node) {
    return {
      src: node.getAttribute('src'),
      poster: node.getAttribute('poster') || '',
    };
  }
}
CustomVideoBlot.blotName = 'customVideo';
CustomVideoBlot.tagName = 'video';
Quill.register(CustomVideoBlot);

const NewsWrite = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  
  const [headerId, setHeaderId] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [headerModalOpen, setHeaderModalOpen] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({ headerId: false, title: false, content: false });
  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [isNotice, setIsNotice] = useState(false);
  const [videoLimitModalOpen, setVideoLimitModalOpen] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const uploadRefs = useRef({
    images: [],
    videos: [],
    cleaned: false,
    saved: false,
  });

  // 수정 모드 여부
  const isEditMode = Boolean(id);

  // 초기 데이터 설정 (수정 모드일 때)
  useEffect(() => {
    if (!isEditMode) return;

    // 현재 로그인 유저 ID 확인 (localStorage 직접 조회)
    let currentUserId = null;
    let currentUserRole = null;
    try {
      const raw = window.localStorage.getItem('currentUser');
      if (raw) {
        const parsed = JSON.parse(raw);
        currentUserId = parsed.id || parsed.user_id || parsed.userId;
        currentUserRole = parsed.role || null;
      }
    } catch (e) {
      // ignore
    }

    // 작성자 본인 확인 함수
    const checkOwner = (authorId) => {
      if (currentUserRole === 'ADMIN') {
        return true;
      }
      if (!currentUserId || !authorId || String(currentUserId) !== String(authorId)) {
        setPermissionModalOpen(true);
        return false;
      }
      return true;
    };

    // 1. state로 전달받은 데이터가 있으면 우선 사용
    if (location.state?.news) {
      const { news } = location.state;
      const authorId = news.author_id || news.authorId;
      
      // 권한 체크
      if (!checkOwner(authorId)) return;

      setHeaderId(news.header_id || news.headerId || '');
      setTitle(news.title || '');
      setContent(news.content || '');
      setIsNotice(Boolean(news.is_notice || news.isNotice));
      setDisplayName(news.display_name || news.displayName || '');
    } else {
      // 2. state가 없으면(새로고침 등) API로 조회
      const fetchDetail = async () => {
        try {
          const res = await getNewsDetailApi(id);
          const data = res?.data || res;
          if (data) {
            const authorId = data.author_id || data.authorId;
            // 권한 체크
            if (!checkOwner(authorId)) return;

            setHeaderId(data.header_id || data.headerId || '');
            setTitle(data.title || '');
            setContent(data.content || '');
            setIsNotice(Boolean(data.is_notice || data.isNotice));
            setDisplayName(data.display_name || data.displayName || '');
          }
        } catch (e) {
          console.error('Failed to load news detail:', e);
          if (e?.status === 401 || e?.suppressAlert) return;
          window.alert('게시글 정보를 불러오지 못했습니다.')
          navigate('/news');
        }
      };
      fetchDetail();
    }
  }, [isEditMode, id, location.state, navigate]);

  // 말머리
  const [headers, setHeaders] = useState([]);

  const quillRef = useRef(null);

  // 이미지 핸들러
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.setAttribute('multiple', 'multiple');
    input.click();

    input.onchange = async () => {
      const files = Array.from(input.files || []);
      if (!files.length) return;

      try {
        const editor = quillRef.current.getEditor();
        const range = editor.getSelection();
        let index = range ? range.index : editor.getLength();

        for (const file of files) {
          const formData = new FormData();
          formData.append('image', file);

          const res = await uploadNewsImageApi(formData);
          const fullUrl = `${getServerBaseUrl()}${res.data.url}`;

          editor.insertEmbed(index, 'image', fullUrl);
          index += 1;
          uploadRefs.current.images.push(fullUrl);
        }

        editor.setSelection(index);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        if (e?.status === 401 || e?.suppressAlert) return;
        window.alert('이미지 업로드 실패');
      }
    };
  }, []);

  // 영상 핸들러
  const videoHandler = useCallback(() => {
    const editor = quillRef.current?.getEditor?.();
    const hasExistingVideo = editor?.root?.querySelector('video');
    if (hasExistingVideo) {
      setVideoLimitModalOpen(true);
      return;
    }

    const videoInput = document.createElement('input');
    videoInput.setAttribute('type', 'file');
    videoInput.setAttribute('accept', 'video/*');
    videoInput.click();

    videoInput.onchange = async () => {
      const videoFile = videoInput.files?.[0];
      if (!videoFile) return;
      if (videoFile.size > MAX_VIDEO_BYTES) {
        window.alert('동영상은 최대 20MB까지 업로드할 수 있습니다.');
        return;
      }

      try {
        const formData = new FormData();
        formData.append('video', videoFile);

        const res = await uploadNewsVideoApi(formData);
        const body = res?.data || res || {};
        const videoUrl = body.url || body.videoUrl || body.file_url || body.fileUrl;
        const posterUrl = body.thumbnailUrl || body.poster || body.posterUrl;

        if (!videoUrl) {
          window.alert('영상 업로드 결과를 확인할 수 없습니다.');
          return;
        }

        const currentEditor = quillRef.current.getEditor();
        const range = currentEditor.getSelection();
        const index = range ? range.index : currentEditor.getLength();

        currentEditor.insertEmbed(index, 'customVideo', {
          src: videoUrl.startsWith('http') ? videoUrl : `${getServerBaseUrl()}${videoUrl}`,
          poster: posterUrl
            ? posterUrl.startsWith('http') ? posterUrl : `${getServerBaseUrl()}${posterUrl}`
            : '',
        });
        currentEditor.setSelection(index + 1);
        uploadRefs.current.videos.push(
          videoUrl.startsWith('http') ? videoUrl : `${getServerBaseUrl()}${videoUrl}`,
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        if (e?.status === 401 || e?.suppressAlert) return;
        window.alert(e.message || '영상 업로드 실패');
      }
    };
  }, []);

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image', 'video'],
        ['clean'],
      ],
      handlers: {
        image: imageHandler,
        video: videoHandler,
      },
    },
    imageResize: {
      parchment: Quill.import('parchment'),
      // quill-image-resize-module-react(v3)에서 내부적으로 modules.find(...)를 사용합니다.
      // 미지정 시(=undefined) 이미지 선택/툴바(삭제 등) 동작에서 런타임 에러가 날 수 있어 명시합니다.
      modules: ['Resize', 'DisplaySize', 'Toolbar'],
    },
  }), [imageHandler, videoHandler]);
  
  const quillFormats = [
    "header",
    "bold", "italic", "underline",
    "color", "background",
    "list", "bullet",
    "link", "image", "customVideo"
  ];

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem('currentUser');
      if (raw) {
        const user = JSON.parse(raw);
        setCurrentUser(user);
        // 관리자일 때 기본값으로 사용자 이름 설정 (수정 모드가 아니고 displayName이 비어있을 때만)
        if (user?.role === 'ADMIN' && !isEditMode && !displayName) {
          setDisplayName(user.name || '');
        }
      } else {
        setCurrentUser(null);
      }
    } catch {
      setCurrentUser(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanupUploads = useCallback(async () => {
    const { images, videos, cleaned, saved } = uploadRefs.current;
    const files = [...images, ...videos];
    if (cleaned || saved || files.length === 0) return;
    try {
      await cleanupNewsUploadsApi(files);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('cleanup uploads failed', e);
    } finally {
      uploadRefs.current.cleaned = true;
    }
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const { images, videos, cleaned, saved } = uploadRefs.current;
      const files = [...images, ...videos];
      if (cleaned || saved || files.length === 0) return;
      try {
        const payload = JSON.stringify({ files });
        const url = `${getServerBaseUrl()}/api/news/uploads/cleanup`;
        navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
        uploadRefs.current.cleaned = true;
      } catch (e) {
        // ignore
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      cleanupUploads();
    };
  }, [cleanupUploads]);

  // 말머리 목록 로딩
  const loadHeaders = async () => {
    try {
      const res = await getNewsHeadersApi();
      const list = Array.isArray(res?.data) ? res.data : res;
      const normalized = (list || []).map((h) => ({
        ...h,
        useScope: h.useScope || h.use_scope || 'ALL',
      }));
      setHeaders(normalized);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to load news headers:', e);
    }
  };

  useEffect(() => {
    loadHeaders();
  }, []);

  const isAdmin = currentUser?.role === 'ADMIN';

  // 뒤로가기
  const handleBack = () => {
    navigate(-1);
  };

  // 말머리 편집 핸들러
  const handleChangeHeaderName = (index, newName) => {
    setHeaders((prev) =>
      prev.map((header, i) => (i === index ? { ...header, name: newName } : header)),
    );
  };

  const handleRemoveHeader = (index) => {
    setHeaders((prev) => {
      const removed = prev[index];
      const next = prev.filter((_, i) => i !== index);
      // 선택된 말머리를 삭제한 경우 선택 해제
      if (removed && removed.headerId === headerId) {
        setHeaderId('');
      }
      return next;
    });
  };

  const handleAddHeader = () => {
    setHeaders((prev) => [
      ...prev,
      { headerId: Date.now(), name: '' },
    ]);
  };

  const handleSubmitHeaders = async () => {
    // 백엔드 연동 후 목록 갱신
    await loadHeaders();
    setHeaderModalOpen(false);
  };

  // 게시글 등록/수정
  const handleSubmit = () => {
    // 필수값 검증: 팝업 없이 빨간 테두리만 표시
    // 태그 제거 후 내용이 비어있는지 체크
    const plainText = content.replace(/<[^>]*>/g, '').trim();
    // 이미지가 있는 경우(img 태그)는 내용이 있는 것으로 간주
    const hasImage = content.includes('<img');
    const hasVideo = content.includes('<video');

    const nextErrors = {
      headerId: !headerId,
      title: !title.trim(),
      content: !plainText && !hasImage && !hasVideo,
    };

    setErrors(nextErrors);

    if (nextErrors.headerId || nextErrors.title || nextErrors.content) {
      return;
    }

    setSaveModalOpen(true);
  };

  const processSubmit = async () => {
    setSaveModalOpen(false);

    try {
      setSubmitting(true);
      
      let newsId;
      
      const payload = {
        headerId,
        title: title.trim(),
        content,
      };
      if (isAdmin) {
        payload.isNotice = isNotice;
        if (displayName && displayName.trim()) {
          payload.displayName = displayName.trim();
        }
      }

      if (isEditMode) {
        // 수정
        await updateNewsApi(id, payload);
        newsId = id;
      } else {
        // 등록
        const res = await createNewsApi(payload);
        const created = res?.data || res;
        newsId = created?.news_id || created?.newsId;
      }

      if (newsId) {
        navigate(`/news/${newsId}`);
        uploadRefs.current.saved = true;
        uploadRefs.current.cleaned = true;
        uploadRefs.current.images = [];
        uploadRefs.current.videos = [];
      } else {
        navigate('/news');
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to save news:', err);
      if (err?.status === 401 || err?.suppressAlert) return;
      window.alert(err.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          maxWidth: 430,
          mx: 'auto',
          bgcolor: 'white',
          minHeight: '100vh',
          borderRadius: 0,
          boxShadow: '0 0 20px rgba(0,0,0,0.06)',
          px: 2,
          position: 'relative',
          pb: 10, 
        }}
      >
        {/* 헤더 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 60,
            borderBottom: '1px solid #e0e0e0',
            mb: 3,
            mx: -2, 
            px: 2,
            position: 'relative',
          }}
        >
          <IconButton
            onClick={handleBack}
            sx={{ p: 0, position: 'absolute', left: 16 }}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 20, color: '#333' }} />
          </IconButton>
          <Typography
            sx={{
              flex: 1,
              textAlign: 'center',
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            {isEditMode ? '글수정' : '글쓰기'}
          </Typography>
        </Box>

        {/* 폼 영역 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          
          {/* 말머리 */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, whiteSpace: 'nowrap', minWidth: 48 }}
              >
                말머리
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 1 }}>
                <FormControl
                  fullWidth
                  size="small"
                  error={errors.headerId}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#fff',
                      '& .MuiSelect-select': {
                        py: 1.2,
                        fontSize: 14,
                      },
                    },
                  }}
                >
                  <InputLabel id="news-header-select-label">말머리 선택</InputLabel>
                  <Select
                    labelId="news-header-select-label"
                    value={headerId}
                    label="말머리 선택"
                    onChange={(e) => {
                      setHeaderId(e.target.value);
                      if (errors.headerId) setErrors((p) => ({ ...p, headerId: false }));
                    }}
                    MenuProps={{
                      disableScrollLock: true,
                      PaperProps: { sx: { maxHeight: 200 } },
                    }}
                  >
                    {(headers || [])
                      .filter((opt) => {
                        const scope = (opt.useScope || opt.use_scope || 'ALL') || 'ALL';
                        if (isAdmin) return true;
                        return String(scope).toUpperCase() === 'ALL';
                      })
                      .map((opt) => (
                      <MenuItem key={opt.headerId} value={opt.headerId} sx={{ fontSize: 14 }}>
                        {opt.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* 관리자 => 추가버튼*/}
                {isAdmin && (
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{
                      minWidth: 0,
                      px: 1.5,
                      height: 40,
                      borderRadius: 1,
                      fontSize: 13,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      borderColor: '#d0d0d0',
                      color: '#000',
                      '&:hover': {
                        borderColor: '#b0b0b0',
                        bgcolor: '#f8f8f8',
                      },
                    }}
                    onClick={() => setHeaderModalOpen(true)}
                  >
                    추가
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          {/* 제목 */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, whiteSpace: 'nowrap', minWidth: 48 }}
              >
                제목
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder=""
                value={title}
                error={errors.title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (errors.title) setErrors((p) => ({ ...p, title: false }));
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: 14,
                    '& input': { py: 1.2 },
                  },
                }}
              />
            </Box>
          </Box>

          {/* 내용 (React Quill) */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              내용
            </Typography>
            <Box
              sx={{
                '& .quill': {
                  width: '100%',
                  marginRight: 0,
                },
                ...(errors.content
                  ? {
                      '& .ql-toolbar.ql-snow': { borderColor: '#d32f2f' },
                      '& .ql-container.ql-snow': { borderColor: '#d32f2f' },
                    }
                  : {}),
                '& .ql-toolbar.ql-snow .ql-formats': {
                  marginRight: '0px',
                },
                '& .ql-toolbar.ql-snow .ql-formats:last-of-type': {
                  marginRight: 0,
                },
                '& .ql-container': {
                  minHeight: 300,
                  fontSize: 14,
                },
              }}
            >
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={content}
                onChange={(v) => {
                  setContent(v);
                  if (errors.content) setErrors((p) => ({ ...p, content: false }));
                }}
                modules={quillModules}
                formats={quillFormats}
                placeholder="내용을 작성해주세요."
              />
            </Box>
          </Box>

          {/* 이름 설정 (관리자만) */}
          {isAdmin && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, whiteSpace: 'nowrap', minWidth: 48 }}
                >
                  작성자
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="이름을 입력하세요 (선택사항)"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: 14,
                      '& input': { py: 1.2 },
                    },
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>

        {isAdmin && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={isNotice}
                onChange={(e) => setIsNotice(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption">
                공지 글 상단 노출
              </Typography>
            }
            sx={{ mt: 1 }}
          />
        )}

        {/* 등록 버튼 */}
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={submitting}
            sx={{
              py: 1.2,
              bgcolor: '#0d47a1',
              fontWeight: 600,
              '&:hover': {
                bgcolor: '#0b3a81',
              },
            }}
          >
            {isEditMode ? '수정' : '등록'}
          </Button>
        </Box>

        {/* 말머리 수정 모달 (관리자용) */}
        {isAdmin && (
          <NewsHeaderEditModal
            open={headerModalOpen}
            headers={headers}
            onChangeHeaderName={handleChangeHeaderName}
            onRemoveHeader={handleRemoveHeader}
            onAddHeader={handleAddHeader}
            onSubmit={handleSubmitHeaders}
            onClose={() => setHeaderModalOpen(false)}
          />
        )}

        {/* 저장/수정 확인 모달 */}
        <ConfirmModal
          open={saveModalOpen}
          message={isEditMode ? '게시글을 수정하시겠습니까?' : '게시글을 등록하시겠습니까?'}
          confirmText={isEditMode ? '수정' : '등록'}
          onConfirm={processSubmit}
          onCancel={() => setSaveModalOpen(false)}
        />
        <ConfirmModal
          open={permissionModalOpen}
          message="수정 권한이 없습니다."
          onConfirm={() => {
            setPermissionModalOpen(false);
            navigate(PATHS.newsList);
          }}
        />
        <ConfirmModal
          open={videoLimitModalOpen}
          message="동영상은 1개만 첨부할 수 있습니다."
          onConfirm={() => setVideoLimitModalOpen(false)}
        />
      </Box>
    </Box>
  );
};

export default NewsWrite;
